import { LightningElement, api, wire } from "lwc";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import type { RankData } from "./types";
import * as locales from "./locales";
import { I18n } from "@devforce/trailhead-client-locale";
import { getAssetsUrl } from "common/helpers";
import scout from "../../common/assets/scout.png";
import hiker from "../../common/assets/hiker.png";
import explorer from "../../common/assets/explorer.png";
import adventurer from "../../common/assets/adventurer.png";
import mountaineer from "../../common/assets/mountaineer.png";
import expeditioner from "../../common/assets/expeditioner.png";
import ranger from "../../common/assets/ranger.png";
import double_star_ranger from "../../common/assets/double_star_ranger.png";
import triple_star_ranger from "../../common/assets/triple_star_ranger.png";
import four_star_ranger from "../../common/assets/four_star_ranger.png";
import five_star_ranger from "../../common/assets/five_star_ranger.png";
import all_star_ranger from "../../common/assets/all_star_ranger.png";
import {
  createVisibilityObserver,
  disconnectObserver,
} from "common/visibilityHelper";
import { trackUserActivity, type UserActivityEvents } from "common/track";

const RANK_IMAGE_MAP = {
  scout: getAssetsUrl(scout),
  hiker: getAssetsUrl(hiker),
  explorer: getAssetsUrl(explorer),
  adventurer: getAssetsUrl(adventurer),
  mountaineer: getAssetsUrl(mountaineer),
  expeditioner: getAssetsUrl(expeditioner),
  ranger: getAssetsUrl(ranger),
  double_star_ranger: getAssetsUrl(double_star_ranger),
  triple_star_ranger: getAssetsUrl(triple_star_ranger),
  four_star_ranger: getAssetsUrl(four_star_ranger),
  five_star_ranger: getAssetsUrl(five_star_ranger),
  all_star_ranger: getAssetsUrl(all_star_ranger),
};

const i18n = new I18n({ rankUpCelebrationPage: locales });

export default class RankUpCelebrationPage extends LightningElement {
  @api rankData: RankData | null = null;

  private rankName = "";
  private showCelebration = false;
  private visibilityObserver: IntersectionObserver | null = null;
  private visible = false;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  renderedCallback() {
    this.checkRankUp();
    this.setupVisibility();
  }

  disconnectedCallback() {
    disconnectObserver(this.visibilityObserver);
  }

  private get labelOptions() {
    const usersName = this.rankData?.name;

    return {
      ranger_rank_title: {
        name: usersName,
      },
      all_star_ranger_title: {
        name: usersName,
      },
    };
  }

  private checkRankUp() {
    const rankString = this.rankData?.rank;

    if (rankString) {
      // API returns "rank_up__<rank_name>", keep this here in case we consume
      // the response string directly or parse out in a wrapping component (i.e. React in th-web, etc.)
      const extractedRank = rankString.replace("rank_up__", "");

      if (extractedRank in RANK_IMAGE_MAP) {
        this.rankName = extractedRank;
        this.showCelebration = true;
      }
    }
  }

  private setupVisibility() {
    if (!this.visibilityObserver && !this.visible) {
      const handleVisible = () => {
        this.visible = true;
        this.track("celebration_page_viewed");
      };

      this.visibilityObserver = createVisibilityObserver(
        this.template.host,
        handleVisible,
        {}
      );
    }
  }

  private get rankSubtitle() {
    return i18n.t("rankUpCelebrationPage", `${this.rankName}_subtitle`);
  }

  private get rankImg() {
    const rankKey = this.rankName as keyof typeof RANK_IMAGE_MAP;
    return RANK_IMAGE_MAP[rankKey] || "";
  }

  private get rankTitle() {
    if (this.rankName == "ranger") {
      return this.labels.ranger_rank_title;
    } else if (this.rankName == "all_star_ranger") {
      return this.labels.all_star_ranger_title;
    } else if (this.rankName.includes("_ranger")) {
      return this.labels.new_ranger_rank_title;
    } else {
      return this.labels.trailblazer_rank_title;
    }
  }

  private get hasRankData() {
    return !!this.rankData;
  }

  private get hasValidRank() {
    return this.showCelebration;
  }

  private track(eventName: keyof UserActivityEvents) {
    trackUserActivity(this, eventName, {
      pageLocation: "th-rank-up-celebration-page",
      type: "rank_up",
      milestone: this.rankName,
    });
  }
}
